import React, {useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {classes, style} from './doppe-action-type-video-widget.st.css';
import {DoppeActionTypeVideo, doppeActionTypeVideoResolveSettingsFromBoundEntity} from '../doppe-action-type-video';
import {
    evaluateFunction,
    urlCreateQueryString,
    VideoServices,
    videoServicesResolveVideoSource
} from '@wix/devzai-utils-common';
import {AnimatedAutoSizedElement, ButtonType, RatioMaintainer, useRootWindow} from '@wix/devzai-utils-react';
import {domLoadScript} from '@wix/devzai-utils-dom';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {doppeHideableValueIsVisibleAndNotEqualValue} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalVideoProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    BymoPageViewerMode,
    useBymoPageViewerContext
} from '../../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {DoppeDtoActionViewType} from '../../../client-server-common/types/doppe-dto-action';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";

export const DoppeActionTypeVideoWidget = React.memo(function DoppeActionTypeVideoWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeVideo.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const viewType = actionContext.getActionViewType();
    const pageViewerContext = useBymoPageViewerContext();
    const actionSettings = actionContext.action.settings;

    const showActionHeader = useMemo(() => {
        return actionContext.action.showActionHeader !== undefined ? actionContext.action.showActionHeader : true;
    }, [actionContext.action]);


    const {
        muteVideo,
        autoplay
    } = evaluateFunction(() => {

        if (pageViewerContext.mode === BymoPageViewerMode.Preview) {
            return {
                muteVideo: true,
                autoplay: false
            }
        } else if (viewType === DoppeDtoActionViewType.ActionButton) {
            return {
                muteVideo: false,
                autoplay: true
            }
        } else {

            const mutedAutoplay = actionSettings.videoPlayerSettings.autoplay;

            return {
                muteVideo: mutedAutoplay,
                autoplay: mutedAutoplay
            }
        }
    })

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeVideoBindableWidget
                reportGoalAchieved={() => {
                    actionContext.reportGoalAchieved()
                }}
                showActionHeader={showActionHeader}
                actionId={actionContext.action.id}
                actionName={actionContext.action.name}
                actionSettings={
                    {
                        ...actionContext.action.settings,
                        videoPlayerSettings: {
                            ...actionContext.action.settings.videoPlayerSettings,
                            autoplay: autoplay,
                            muteVideo: muteVideo
                        }
                    }}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeVideoBindableWidget = React.memo(function DoppeActionTypeVideoBindableWidget(
    props: {
        actionSettings: DoppeActionTypeVideo.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
        actionName: string;
        showActionHeader?: boolean;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
        actionName,
        showActionHeader = true,
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();

    const {
        videoProvider,
        videoProviderId
    } = actionSettings;


    if (videoProvider !== null && videoProviderId !== null) {
        const boundService = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalVideoProviderResolveBusinessEntityReference(videoProvider, videoProviderId))

        if (!boundService || doppeBusinessEntityDtoIsNotFound(boundService)) {
            return (
                <DoppeActionTypeVideoWidgetContentUnavailable
                    reason={
                        !boundService ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            const resolvedActionSettings = doppeActionTypeVideoResolveSettingsFromBoundEntity(actionSettings, boundService);
            const videoTitle = doppeHideableValueIsVisibleAndNotEqualValue(resolvedActionSettings.title, '') ? resolvedActionSettings.title : actionName;

            return (
                <DoppeActionTypeVideoWidgetInner
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={resolvedActionSettings}
                    videoTitle={videoTitle}
                    showActionHeader={showActionHeader}
                />
            )
        }
    } else {
        const videoTitle = doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.title, '') ? actionSettings.title : actionName;

        return (
            <DoppeActionTypeVideoWidgetInner
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
                videoTitle={videoTitle}
                showActionHeader={showActionHeader}
            />
        )
    }
});


export const DoppeActionTypeVideoWidgetContentUnavailable = React.memo(function DoppeActionTypeServiceWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitleService']}
        />
    )
});


export const DoppeActionTypeVideoWidgetInner = React.memo(function DoppeActionTypeVideoWidgetInner(
    props: {
        actionSettings: DoppeActionTypeVideo.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
        videoTitle?: string;
        showActionHeader?: boolean;
    }
) {

    const {
        actionSettings,
        reportGoalAchieved,
        videoTitle,
        showActionHeader = true
    } = props;

    const actionTitle = videoTitle;

    const videoUrl = actionSettings.externalVideoUrl;
    const videoServiceSource = useMemo(() => {
        return videoServicesResolveVideoSource(videoUrl);
    }, [videoUrl])

    const rootWindow = useRootWindow();

    useEffect(() => {
        if (videoServiceSource?.videoService === VideoServices.TikTok) {
            domLoadScript('https://www.tiktok.com/embed.js', {rootWindow: rootWindow}).catch(() => {
            });
        }
    }, [videoServiceSource])

    const [hostname, setHostname] = useState<string>();
    useLayoutEffect(() => {
        setHostname(window.location.hostname)
    }, [])


    const {
        videoButtonText,
        description,
        title,
        externalVideoUrl,
        videoPlayerSettings: {
            autoplay,
            muteVideo,
        },
    } = actionSettings;

    const showInnerContent =
        doppeHideableValueIsVisibleAndNotEqualValue(title, '') ||
        doppeHideableValueIsVisibleAndNotEqualValue(description, '') ||
        doppeHideableValueIsVisibleAndNotEqualValue(videoButtonText, '');

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapper
            fullWidthMainMedia={true}
            mainMedia={<div
                className={style(classes.videoContent, {showInnerContent: showInnerContent, showActionHeader: showActionHeader})}
            >
                {evaluateFunction(() => {

                    if (videoServiceSource) {
                        switch (videoServiceSource.videoService) {
                            case VideoServices.Youtube: {

                                const videoId = videoServiceSource.videoId;

                                return (
                                    <RatioMaintainer
                                        widthHeightRatio={16 / 9}
                                    >
                                        <iframe
                                            className={style(classes.iframe, {})}
                                            src={`https://www.youtube.com/embed/${videoId}?${urlCreateQueryString({
                                                autoplay: autoplay ? '1' : '0',
                                                mute: muteVideo ? '1' : '0',
                                                playsinline: '1',
                                                modestbranding: '1',
                                                origin: hostname,
                                                iv_load_policy: '3',
                                                rel: '0',
                                                start: '0',
                                                // cc_load_policy: '0',
                                                // fs: '0'
                                            }, false)}`}
                                            allow={`fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                            frameBorder="0"
                                            title={actionTitle}
                                        />
                                    </RatioMaintainer>
                                )
                            }
                            case VideoServices.YoutubePlaylist: {

                                const videoId = videoServiceSource.videoId;

                                return (
                                    <RatioMaintainer
                                        widthHeightRatio={1}
                                    >
                                        <iframe
                                            src={`https://www.youtube.com/embed/videoseries?${urlCreateQueryString({
                                                list: videoId,
                                                autoplay: autoplay ? '1' : '0',
                                                mute: muteVideo ? '1' : '0',
                                                playsinline: '1',
                                                modestbranding: '1',
                                                origin: hostname,
                                                iv_load_policy: '3',
                                                rel: '0',
                                                start: '0',
                                                // cc_load_policy: '0',
                                                // fs: '0'
                                            }, false)}`}
                                            title={actionTitle}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen>
                                        </iframe>
                                    </RatioMaintainer>
                                )
                            }
                            case VideoServices.Vimeo: {

                                const videoId = videoServiceSource.videoId;

                                return (
                                    <RatioMaintainer
                                        widthHeightRatio={16 / 9}
                                    >
                                        <iframe
                                            src={`https://player.vimeo.com/video/${videoId}?${urlCreateQueryString({
                                                autoplay: autoplay ? '1' : '0',
                                                muted: muteVideo ? '1' : '0',
                                                playsinline: '1',
                                                portrait: '0',
                                                title: '0',
                                                byline: '0',
                                                autopause: '0'
                                            }, false)}`}
                                            allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                            frameBorder="0"
                                            title={actionTitle}
                                        />
                                    </RatioMaintainer>
                                )
                            }
                            case VideoServices.TikTok: {
                                return (
                                    <AnimatedAutoSizedElement
                                        durationMilliseconds={400}
                                    >
                                        <blockquote
                                            className={style(classes.tiktokEmbed, 'tiktok-embed')}
                                            cite={`https://www.tiktok.com/${videoServiceSource.user}/video/${videoServiceSource.videoId}`}
                                            data-video-id={videoServiceSource.videoId}
                                        >
                                            <section/>
                                        </blockquote>
                                    </AnimatedAutoSizedElement>

                                )
                            }
                        }
                    }

                    return (
                        <RatioMaintainer
                            widthHeightRatio={16 / 9}
                        >
                            <div className={style(classes.notSupportedVideo, {})}>Not Supported Video</div>
                        </RatioMaintainer>
                    )
                })}
            </div>}
            footer={doppeHideableValueIsVisibleAndNotEqualValue(videoButtonText, '') ? (
                <DoppeActionTypeWidgetInnerButton
                    className={style(classes.linkButton)}
                    buttonType={ButtonType.NormalButton}
                    tagName={'a'}
                    href={externalVideoUrl}
                    target={'_blank'}
                    onClick={() => {
                        reportGoalAchieved();
                    }}
                >
                    {videoButtonText}
                </DoppeActionTypeWidgetInnerButton>
            ) : undefined}
        >
            <DoppeActionTypeWidgetInnerTitle>
                {title}
            </DoppeActionTypeWidgetInnerTitle>
            <DoppeActionTypeWidgetInnerDescription>
                {description}
            </DoppeActionTypeWidgetInnerDescription>
        </DoppeActionTypeWidgetContentLayoutNewWrapper>
    );
});
